import { Injectable } from '@angular/core';
import { ApiClient } from '../api-client.http.service';
import { ApplicationMenuViewItemDto } from '../dto/Application-view-menu.dto';
import { Observable } from 'rxjs';
import { ApiResponse, ApplicationApiResponse } from '../dto/api-response.dto';
import { AppNoteModel, CreateNoteCommand } from '@shared/models/app-note.model';
import {
  ApplicationUrls,
  DocumentManagementUrl,
  MemberUrls,
} from '@core/constants';
import { DocumentPaginatedResponseDto, PaginatedResponseDto } from '../dto';
import { AppNoteApiRequest } from '@shared/models/requests/app-note-request.dto';
import { ApplicationAssetResponse } from '../dto/application-asset-response.dto';
import { ApplicationChangeLogDto } from '../dto/application-change-log-response.dto';
import { ApplicationChangeLogApiRequest } from '@shared/models/requests/application-change-log-request.dto';
import { AssetCategoryResponse } from '../dto/asset-type-response.dto';
import { AppNoteDto } from '../dto/app-note.dto';
import { ApplicationProduct } from '../dto/applicationproduct.dto';
import {
  ConditionAndPolicy,
  SitesConditionAndPolicy,
} from '../dto/condition-policy.dto';
import { ConditionPolicy } from '@shared/models/condition-policy.model';
import {
  ApplicationLiabilityResponse,
  importLiabilityRequest,
} from '../dto/application-liability-response.dto';
import { ProductTerm } from '@shared/models/term-model';
import { UpdateApplicationProductTermRequest } from '@shared/models/requests/update-product-term.request.dto';
import { PaymentFrequency } from '@shared/models/payment-frequency.model';
import { Fees } from '@shared/models/fees.model';
import {
  ApplicantDocRequest,
  DocumentRequest,
  DocumentSignerRequest,
  DocumentSigningRequest,
} from '@shared/models/requests/documents.request.dto';
import {
  ApiResponseDTO,
  ApplicantDocumentResponseDTO,
} from '../dto/documentFields.dto';
import { Applicant } from '../dto/applicant.dto';
import {
  ApplicantIdentityVerificationDto,
  ApplicantIdentityVerificationRequestDto,
} from '../dto/applicant-identity-verification.dto';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import {
  GetApplicationProductTermDetailResponse,
  UpdateApplicationProductTermDetailRateRequest,
  UpdateApplicationProductTermDetailRateResponse
} from '../dto/application-product-term-detail.dto';
@Injectable({
  providedIn: 'any',
})
export class ApplicationViewApiService {
  constructor(private api: ApiClient) {}
  getApplicationSummary(
    applicationId: number
  ): Observable<ApplicationApiResponse<ApplicationMenuViewItemDto>> {
    return this.api.get<ApplicationApiResponse<ApplicationMenuViewItemDto>>(
      ApplicationUrls.applicationSummary(applicationId)
    );
  }
  private getSiteIdFromLocalStorage(): string | null {
    try {
      const raw = localStorage.getItem('siteConfiguration');
      if (!raw) {return null;}
      const config = JSON.parse(raw) as { siteId?: string };
      return config?.siteId ?? null;
    } catch {
      return null;
    }
  }
  setNote(note: CreateNoteCommand): Observable<AppNoteModel> {
    return this.api.post<AppNoteModel>(ApplicationUrls.notes, note);
  }

  getNotes(
    request: AppNoteApiRequest
  ): Observable<DocumentPaginatedResponseDto<AppNoteDto>> {
    return this.api.get<DocumentPaginatedResponseDto<AppNoteDto>>(
      ApplicationUrls.notes,
      request
    );
  }
  getNote(id: number): Observable<AppNoteDto> {
    return this.api.get<AppNoteDto>(ApplicationUrls.note(id));
  }
  deleteNote(id: number): Observable<any> {
    return this.api.delete<any>(ApplicationUrls.note(id));
  }

  updateNote(id: number, request: CreateNoteCommand): Observable<AppNoteModel> {
    return this.api.put<any>(ApplicationUrls.note(id), request);
  }
  getAssetByApplicationId(
    applicationId: number
  ): Observable<ApiResponse<ApplicationAssetResponse[]>> {
    return this.api.get<ApiResponse<ApplicationAssetResponse[]>>(
      ApplicationUrls.applicationAsset(applicationId)
    );
  }

  getAssetById(
    assetId: any,
    applicantId: any
  ): Observable<ApiResponse<ApplicationAssetResponse>> {
    return this.api.get<ApiResponse<ApplicationAssetResponse>>(
      ApplicationUrls.GetAssetsById(assetId, applicantId)
    );
  }

  getChangeLogByApplicationId(
    request: ApplicationChangeLogApiRequest
  ): Observable<PaginatedResponseDto<ApplicationChangeLogDto>> {
    return this.api.get<PaginatedResponseDto<ApplicationChangeLogDto>>(
      ApplicationUrls.applicationLog,
      request
    );
  }

  getAssetGroupByApplicationId(
    applicationId: number
  ): Observable<ApiResponse<ApplicationAssetResponse>> {
    return this.api.get<ApiResponse<ApplicationAssetResponse>>(
      ApplicationUrls.applicationAssetByGroup(applicationId)
    );
  }

  getAllAssetTypes(): Observable<ApiResponse<AssetCategoryResponse[]>> {
    return this.api.get<ApiResponse<AssetCategoryResponse[]>>(
      ApplicationUrls.getAllAssetTypes
    );
  }

  deleteApplicantAsset(request: any): Observable<any> {
    const payload: any = { ...request, IsRequestTeamPortal: true };
    return this.api.delete<any>(ApplicationUrls.DeleteApplicantAsset, payload);
  }

  addOrUpdateApplicantAsset(request: any): Observable<any> {
    const payload: any = { ...request, IsRequestTeamPortal: true };
    return this.api.post<any>(
      ApplicationUrls.AddOrUpdateApplicantAsset,
      payload
    );
  }

  getapplicationProductsByApplicationId(
    applicationId: number
  ): Observable<ApiResponse<ApplicationProduct>> {
    return this.api.get<ApiResponse<ApplicationProduct>>(
      ApplicationUrls.applicationProducts(applicationId)
    );
  }

  getConditionAndPolicyByApplicationProductId(
    applicationProductId: number
  ): Observable<ApiResponse<ConditionAndPolicy>> {
    return this.api.get<ApiResponse<ConditionAndPolicy>>(
      ApplicationUrls.conditionAndPolicyByApplicationProductId(
        applicationProductId
      )
    );
  }

  getSitesConditionAndPolicy(): Observable<
    ApiResponse<SitesConditionAndPolicy>
  > {
    return this.api.get<ApiResponse<SitesConditionAndPolicy>>(
      ApplicationUrls.getSitesConditionAndPolicy
    );
  }

  addConditionOrPolicy(
    request: ConditionPolicy
  ): Observable<ApiResponse<ConditionPolicy>> {
    return this.api.post<ApiResponse<ConditionPolicy>>(
      ApplicationUrls.postConditionOrPolicy,
      request
    );
  }

  getApplicationLiabilitiesByGroup(
    applicationId: number
  ): Observable<ApiResponse<ApplicationLiabilityResponse>> {
    return this.api.get<ApiResponse<ApplicationLiabilityResponse>>(
      ApplicationUrls.applicantLibilitiesByGroup(applicationId)
    );
  }

  getLiabilityByApplicantId(applicationId: number): Observable<any> {
    return this.api.get<any>(
      ApplicationUrls.getLiabilityByApplicantId(applicationId)
    );
  }

  getLiabilityById(
    libilityId: any,
    applicantId: any
  ): Observable<ApiResponse<ApplicationLiabilityResponse>> {
    return this.api.get<ApiResponse<ApplicationLiabilityResponse>>(
      ApplicationUrls.GetLiabilityById(libilityId, applicantId)
    );
  }

  deleteApplicantLiability(request: any): Observable<any> {
    return this.api.delete<any>(
      ApplicationUrls.DeleteApplicantLiability,
      request
    );
  }

  addApplicantLiability(request: any): Observable<any> {
    return this.api.post<any>(ApplicationUrls.AddApplicantLibility, request);
  }

  AddOrUpdateMultiLiability(request: any): Observable<any> {
    return this.api.post<any>(
      ApplicationUrls.AddOrUpdateMultiLiability,
      request
    );
  }

  addApplicantLiabilityByCreditReport(request: any): Observable<any> {
    return this.api.post<any>(
      ApplicationUrls.ImportLiabilityFromCreditReport,
      request
    );
  }

  getLiabilityByCreditReport(
    applicationId: number,
    fromImportapplication: boolean = false,
    applicantId?: number
  ): Observable<ApiResponse<importLiabilityRequest>> {
    return this.api.get<ApiResponse<importLiabilityRequest>>(
      ApplicationUrls.GetLiabilityFromCreditReport(applicationId, fromImportapplication, applicantId)
    );
  }

  getAllTerms(productId: number): Observable<ApiResponse<ProductTerm>> {
    return this.api.get<ApiResponse<ProductTerm>>(
      ApplicationUrls.getAllTerms(productId)
    );
  }

  getPaymentFrequency(
    productId: number
  ): Observable<ApiResponse<PaymentFrequency>> {
    return this.api.get<ApiResponse<PaymentFrequency>>(
      ApplicationUrls.getPaymentFrequency(productId)
    );
  }

  getFees(productId: number): Observable<ApiResponse<Fees>> {
    return this.api.get<ApiResponse<Fees>>(
      ApplicationUrls.getProductAdditionalFees(productId)
    );
  }

  getAmortization(productId: number): Observable<ApiResponse<any>> {
    return this.api.get<ApiResponse<any>>(
      ApplicationUrls.getAmortization(productId)
    );
  }

  getLendingDecision(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getApprovalAutoDecision,
      request
    );
  }

  getRateAutoDecision(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getRateAutoDecision,
      request
    );
  }

  getRateCurrentVersion(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getRateCurrentVersion,
      request
    );
  }

  getApprovalCurrentVersion(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getApprovalCurrentVersion,
      request
    );
  }

  getRateOverrideList(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getRateList,
      request
    );
  }

  getPaymnetSummary(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getPaymentSummary,
      request
    );
  }

  getPaymentAmount(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getPaymentAmount,
      request
    );
  }

  updateProductTerm(
    request: UpdateApplicationProductTermRequest
  ): Observable<ApiResponse<any>> {
    return this.api.put<ApiResponse<any>>(
      ApplicationUrls.updateApplicationProductTerm,
      request
    );
  }

  getApplicationProductTermDetail(
    applicationProductId: number,
    requestId?: string
  ): Observable<GetApplicationProductTermDetailResponse> {
    // applicationProductId is already in the URL, only pass requestId if provided
    const params: any = requestId ? { requestId } : undefined;
    return this.api.get<GetApplicationProductTermDetailResponse>(
      ApplicationUrls.getApplicationProductTermDetail(applicationProductId),
      params
    );
  }

  updateApplicationProductTermDetailRate(
    request: UpdateApplicationProductTermDetailRateRequest
  ): Observable<UpdateApplicationProductTermDetailRateResponse> {
    return this.api.put<UpdateApplicationProductTermDetailRateResponse>(
      ApplicationUrls.updateApplicationProductTermDetailRate,
      request
    );
  }

  updateApplicationProduct(request: any): Observable<ApiResponse<any>> {
    // Convert enum values from numbers to strings
    const convertedRequest = this.convertEnumsToStrings({ ...request, IsRequestTeamPortal: true });
    return this.api.put<ApiResponse<any>>(
      ApplicationUrls.updateApplicationProduct,
      convertedRequest
    );
  }

  private convertEnumsToStrings(request: any): any {
    const convertedRequest = JSON.parse(JSON.stringify(request));
    if (convertedRequest.updateApplicationProducts) {
      convertedRequest.updateApplicationProducts.forEach((product: any) => {
        if (product.lendingProductDetails) {
          if (product.lendingProductDetails.approvalMode === 0) {
            product.lendingProductDetails.approvalMode = 'Individual';
          } else if (product.lendingProductDetails.approvalMode === 1) {
            product.lendingProductDetails.approvalMode = 'All';
          }

          if (product.lendingProductDetails.netWorthMode === 0) {
            product.lendingProductDetails.netWorthMode = 'Combined';
          } else if (product.lendingProductDetails.netWorthMode === 1) {
            product.lendingProductDetails.netWorthMode = 'Individual';
          }

          if (product.lendingProductDetails.applicantMode === 0) {
            product.lendingProductDetails.applicantMode = 'Combined';
          } else if (product.lendingProductDetails.applicantMode === 1) {
            product.lendingProductDetails.applicantMode = 'Individual';
          }
        }
      });
    }
    return convertedRequest;
  }

  updateProductAdditionalFees(request: any): Observable<ApiResponse<any>> {
    return this.api.put<ApiResponse<any>>(
      ApplicationUrls.updateProductAdditionalFees,
      request
    );
  }

  getApplicantDocument(applicantId: number): Observable<ApiResponse<any>> {
    return this.api.get<ApiResponse<any>>(
      ApplicationUrls.getApplicantDocumentPortal(applicantId)
    );
  }

  deleteApplicantDocument(request: any): Observable<any> {
    return this.api.delete<any>(
      ApplicationUrls.deleteApplicantDocument,
      request
    );
  }

  downloadApplicantDocument(request: any): Observable<Blob> {
    return this.api.download<Blob>(
      ApplicationUrls.downloadApplicantDocument,
      request
    );
  }

  documentPreviewDownload(request: DocumentRequest): Observable<Blob> {
    return this.api.download<Blob>(
      ApplicationUrls.documentPreviewDownload,
      request
    );
  }

  documentPreview(request: DocumentSignerRequest): Observable<Blob> {
    return this.api.download<Blob>(
      ApplicationUrls.documentPreviewPreview,
      request
    );
  }

  getAllDocumentViaApplicationId(
    applicantionId: number
  ): Observable<ApiResponseDTO> {
    return this.api.get<ApiResponseDTO>(
      ApplicationUrls.getAllDocumentViaApplicationId(applicantionId)
    );
  }

  getAllApplicantDocViaApplicationId(
    request: ApplicantDocRequest
  ): Observable<ApplicantDocumentResponseDTO> {
    return this.api.get<ApplicantDocumentResponseDTO>(
      ApplicationUrls.getAllApplicantDocViaApplicationId,
      request
    );
  }

  getAllApplicants(
    applicationId: number
  ): Observable<ApiResponse<Applicant[]>> {
    return this.api.get<ApiResponse<Applicant[]>>(
      ApplicationUrls.getAllApplicants(applicationId)
    );
  }

  addApplicantDocument(request: any): Observable<ApiResponse<any>> {
    const payload: any = { ...request, IsRequestTeamPortal: true };
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.addApplicantDocument,
      payload
    );
  }

  getApplicationDocuments(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.getApplicationDocuments,
      request
    );
  }

  documentSigning(
    request: DocumentSigningRequest
  ): Observable<ApiResponse<any>> {
    const payload: any = { ...request, IsRequestTeamPortal: true };
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.documentSigning,
      payload
    );
  }

  downloadDocumentPackage(request: any): Observable<Blob> {
    return this.api.download<Blob>(
      ApplicationUrls.downloadDocumentPackage,
      request
    );
  }

  sendDocumentPackage(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.SendDocumentPackage,
      request
    );
  }

  refreshDocument(request: any): Observable<any> {
    return this.api.get<any>(
      ApplicationUrls.refreshDocument(
        request.applicationIds,
        request.intervalTimeInMin
      )
    );
  }

  getAllDocuments(): Observable<ApiResponse<any>> {
    return this.api.get<ApiResponse<any>>(
      DocumentManagementUrl.getAllDocumentTemplate
    );
  }

  getDocumentConfigurationByDocId(request: any): Observable<any> {
    return this.api.get<any>(
      ApplicationUrls.getDocumentConfigurationByDocId(
        request.documentId,
        request.applicationId
      )
    );
  }

  sendDocument(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.SendDocument,
      request
    );
  }

  sendDocumentDownload(request: any): Observable<Blob> {
    return this.api.download<Blob>(
      ApplicationUrls.sendDocumentDownload,
      request
    );
  }

  documentSendByDocuSign(request: any): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.documentSendByDocuSign,
      request
    );
  }
  sendCompleteStatusMail(applicationId: number): Observable<boolean> {
    return this.api.get<boolean>(
      ApplicationUrls.SendCompleteStatusMail(applicationId)
    );
  }

  sendProductSummaryMail(applicationId: number): Observable<boolean> {
    return this.api.get<boolean>(
      ApplicationUrls.SendProductSummaryMail(applicationId)
    );
  }

  // Identity Verification API methods
  getApplicantIdentityVerification(
    applicantId: number
  ): Observable<ApiResponse<ApplicantIdentityVerificationDto[]>> {
    return this.api.get<ApiResponse<ApplicantIdentityVerificationDto[]>>(
      ApplicationUrls.getApplicantIdentityVerification(applicantId)
    );
  }

  addUpdateDeleteApplicantIdentityVerification(
    request: ApplicantIdentityVerificationRequestDto
  ): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      ApplicationUrls.addUpdateDeleteApplicantIdentityVerification,
      request
    );
  }

  deleteApplicantIdentityVerification(applicantIdentityId: number): Observable<any> {
    return this.api.delete<any>(
      `${ApplicationUrls.deleteApplicantIdentityVerification}?applicantIdentity=${applicantIdentityId}`
    );
  }

  // Email Verification API methods
  sendEmailVerificationLink(mailId: string): Observable<ApiResponse<any>> {
    return this.api.post<ApiResponse<any>>(
      MemberUrls.sendEmailVerificationLink,
      { mailId }
    );
  }

  getPersonIdByMemberNumbers(memberNumbers: string[]): Observable<any> {
    return this.api.get<any>(ApplicationUrls.getPersonNumbers, {
      memberNumber: memberNumbers,
    });
  }

  getApplicantsFromCoreBanking(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });

    return this.api.postParam<ApiResponse<any>>(
      ApplicationUrls.getApplicantsFromCoreBanking,
      request,
      headers
    );
  }

  commitApplicantInCoreBanking(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });
    return this.api.postParam<ApiResponse<any>>(
      ApplicationUrls.commitApplicantInCoreBanking,
      {},
      headers,
      false,
      request
    );
  }

  commitDepositeInCoreBanking(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });
    return this.api.postParam<ApiResponse<any>>(
      ApplicationUrls.commitDepositeInCoreBanking,
      {},
      headers,
      false,
      request
    );
  }

  commitLendingInCoreBanking(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });
    return this.api.postParam<ApiResponse<any>>(
      ApplicationUrls.commitLendingInCoreBanking,
      {},
      headers,
      false,
      request
    );
  }

  updateApplicantInCoreBanking(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });
    return this.api.put<ApiResponse<any>>(
      ApplicationUrls.updateApplicantInCoreBanking,
      request,
      true
    );
  }
  updateApplicant(request: any): Observable<ApiResponse<any>> {
    const siteId = this.getSiteIdFromLocalStorage();
    const headers = new HttpHeaders({
      'X-SITE-ID': siteId,
    });
    return this.api.put<ApiResponse<any>>(
      ApplicationUrls.updateApplicant,
      request,
      headers
    );
  }
}
